import React, { useState } from "react";

import {
  Box,
  Chip,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Stack,
  CircularProgress,
  TableSortLabel,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const PREVIEW_LIMIT = 3;

function AliasPreview({ names = [] }) {
  const [expanded, setExpanded] = useState(false);

  const preview = expanded ? names : names.slice(0, PREVIEW_LIMIT);

  const remaining = names.length - PREVIEW_LIMIT;

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {preview.map((name) => (
          <Chip key={name} label={name} size="small" sx={{ mb: 1 }} />
        ))}
      </Stack>

      {names.length > PREVIEW_LIMIT && (
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded((v) => !v)}
          sx={{ mt: 1 }}
        >
          {expanded ? "Show less" : `+${remaining} more`}
        </Button>
      )}
    </Box>
  );
}

export default function CategoryGroupTable({
  groups = [],
  loading,
  onEdit,
  onDelete,
}) {
  const [sortField, setSortField] = useState("group_name");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sorted = [...groups].sort((a, b) => {
    let av = a[sortField];
    let bv = b[sortField];

    if (sortField === "category_count") {
      av = a.category_names?.length ?? 0;
      bv = b.category_names?.length ?? 0;
    }

    if (typeof av === "number") {
      return sortDir === "asc" ? av - bv : bv - av;
    }

    return sortDir === "asc"
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={2}
        py={6}
      >
        <CircularProgress size={28} />
        <Typography>Loading category groups...</Typography>
      </Box>
    );
  }

  if (!groups.length) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography>No category groups found.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === "group_name"}
                  direction={sortDir}
                  onClick={() => handleSort("group_name")}
                >
                  Group Name
                </TableSortLabel>
              </TableCell>

              <TableCell width={120}>
                <TableSortLabel
                  active={sortField === "category_count"}
                  direction={sortDir}
                  onClick={() => handleSort("category_count")}
                >
                  Aliases
                </TableSortLabel>
              </TableCell>

              <TableCell>Alias Names</TableCell>

              <TableCell width={140}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sorted.map((group) => (
              <TableRow key={group.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{group.group_name}</Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={group.category_names?.length ?? 0}
                    color="primary"
                    size="small"
                  />
                </TableCell>

                <TableCell>
                  <AliasPreview names={group.category_names ?? []} />
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <IconButton color="primary" onClick={() => onEdit(group)}>
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      color="error"
                      onClick={() => onDelete(group.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Showing {groups.length} group
        {groups.length !== 1 ? "s" : ""}
      </Typography>
    </Box>
  );
}
